import { useState, useEffect, useContext, createContext, ReactNode } from 'react';
import { apiClient } from '../../services/api';

interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'SUPER_ADMIN' | 'MODERATOR' | 'MEMBER';
  permissions: string[];
  avatar?: string;
  lastLoginAt: string;
  createdAt: string;
}

interface AuthContextType {
  user: AdminUser | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  checkPermission: (permission: string) => boolean;
  hasRole: (role: AdminUser['role'] | AdminUser['role'][]) => boolean;
  refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAdminAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

interface AdminAuthProviderProps {
  children: ReactNode;
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!token;

  // Initialize auth state on component mount
  useEffect(() => {
    initializeAuth();
  }, []);

  // Auto-refresh token before expiry
  useEffect(() => {
    if (isAuthenticated && token) {
      const refreshInterval = setInterval(async () => {
        try {
          await refreshToken();
        } catch (error) {
          console.warn('Admin token refresh failed:', error);
          logout();
        }
      }, 25 * 60 * 1000); // Refresh every 25 minutes (tokens expire in 30 minutes)

      return () => clearInterval(refreshInterval);
    }
  }, [isAuthenticated, token]);

  const initializeAuth = async () => {
    setIsLoading(true);
    try {
      // ใช้ localStorage keys เดียวกับ regular auth
      const storedToken = localStorage.getItem('access_token');
      const storedRefreshToken = localStorage.getItem('refresh_token');

      if (storedToken) {
        // Verify token with API
        const isValid = await verifyToken(storedToken);
        if (isValid) {
          // ดึง user data จาก /api/auth/me
          const response = await apiClient.get('/api/auth/me', {
            headers: {
              'Authorization': `Bearer ${storedToken}`,
            },
          });

          if (response.status === 200) {
            const userData = response.data;

            // Transform to AdminUser format
            const adminUser: AdminUser = {
              id: userData.id,
              name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email,
              email: userData.email,
              role: userData.role as AdminUser['role'],
              permissions: userData.role === 'SUPER_ADMIN'
                ? Object.values(AdminPermissions)
                : userData.role === 'ADMIN'
                  ? [
                      AdminPermissions.COMPANY_VIEW,
                      AdminPermissions.COMPANY_CREATE,
                      AdminPermissions.COMPANY_UPDATE,
                      AdminPermissions.COMPANY_APPROVE,
                      AdminPermissions.USER_VIEW,
                      AdminPermissions.USER_CREATE,
                      AdminPermissions.ANALYTICS_VIEW,
                    ]
                  : [],
              avatar: userData.avatarUrl,
              lastLoginAt: userData.lastLoginAt || new Date().toISOString(),
              createdAt: userData.createdAt || new Date().toISOString(),
            };

            setToken(storedToken);
            setUser(adminUser);
          } else {
            clearAuthData();
          }
        } else if (storedRefreshToken) {
          // Token invalid, try to refresh
          const refreshSuccess = await refreshToken();
          if (!refreshSuccess) {
            clearAuthData();
          }
        } else {
          // No refresh token, clear storage
          clearAuthData();
        }
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
      clearAuthData();
    } finally {
      setIsLoading(false);
    }
  };

  const verifyToken = async (token: string): Promise<boolean> => {
    try {
      // Use unified auth endpoint (no separate admin endpoint)
      const response = await apiClient.get('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token verification failed:', error);
      return false;
    }
  };

  const login = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    try {
      // Call unified login endpoint (no separate admin login)
      const response = await apiClient.post('/api/auth/login', {
        email: username,  // Changed from username to email
        password
      });

      const data = response.data;

      if (response.status === 200 && data.access_token) {
        const { user, access_token, refresh_token } = data;

        // Transform backend user data to frontend format
        const adminUser: AdminUser = {
          id: user.id,
          name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
          email: user.email,
          role: user.role as AdminUser['role'],
          permissions: user.role === 'SUPER_ADMIN'
            ? Object.values(AdminPermissions)
            : user.role === 'ADMIN'
              ? [
                  AdminPermissions.COMPANY_VIEW,
                  AdminPermissions.COMPANY_CREATE,
                  AdminPermissions.COMPANY_UPDATE,
                  AdminPermissions.COMPANY_APPROVE,
                  AdminPermissions.USER_VIEW,
                  AdminPermissions.USER_CREATE,
                  AdminPermissions.ANALYTICS_VIEW,
                ]
              : [],
          avatar: user.avatarUrl,
          lastLoginAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
        };

        // Store auth data ใช้ keys เดียวกับ regular auth
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);

        setToken(access_token);
        setUser(adminUser);

        return { success: true };
      } else {
        return {
          success: false,
          error: data.detail || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ'
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: 'เกิดข้อผิดพลาดในการเชื่อมต่อ'
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    try {
      // Call unified logout endpoint (no separate admin endpoint)
      if (token) {
        await apiClient.post('/api/auth/logout', {}, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthData();
      setIsLoading(false);
    }
  };

  const clearAuthData = () => {
    // ใช้ keys เดียวกับ regular auth
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
  };

  const refreshToken = async (): Promise<boolean> => {
    if (!token) return false;

    try {
      // Use unified refresh endpoint (no separate admin endpoint)
      const response = await apiClient.post('/api/auth/refresh', {
        refresh_token: localStorage.getItem('refresh_token')
      });

      if (response.status === 200) {
        const data = response.data;
        const newToken = data.access_token;
        const newRefreshToken = data.refresh_token;

        // ใช้ keys เดียวกับ regular auth
        localStorage.setItem('access_token', newToken);
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken);
        }
        setToken(newToken);

        return true;
      }

      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  };

  const checkPermission = (permission: string): boolean => {
    if (!user) return false;

    // Super admin has all permissions
    if (user.role === 'SUPER_ADMIN') return true;

    // ADMIN has permissions based on their permissions array
    if (user.role === 'ADMIN') {
      return user.permissions.includes(permission);
    }

    return user.permissions.includes(permission);
  };

  const hasRole = (role: AdminUser['role'] | AdminUser['role'][]): boolean => {
    if (!user) return false;

    if (Array.isArray(role)) {
      return role.includes(user.role);
    }

    return user.role === role;
  };

  const contextValue: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated,
    login,
    logout,
    checkPermission,
    hasRole,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Permission constants
export const AdminPermissions = {
  // Company Management
  COMPANY_VIEW: 'company:view',
  COMPANY_CREATE: 'company:create',
  COMPANY_UPDATE: 'company:update',
  COMPANY_DELETE: 'company:delete',
  COMPANY_APPROVE: 'company:approve',

  // User Management
  USER_VIEW: 'user:view',
  USER_CREATE: 'user:create',
  USER_UPDATE: 'user:update',
  USER_DELETE: 'user:delete',
  USER_IMPERSONATE: 'user:impersonate',

  // Analytics
  ANALYTICS_VIEW: 'analytics:view',
  ANALYTICS_EXPORT: 'analytics:export',

  // System Management
  SYSTEM_SETTINGS: 'system:settings',
  SYSTEM_BACKUP: 'system:backup',
  SYSTEM_MAINTENANCE: 'system:maintenance',

  // Content Management
  CONTENT_MANAGE: 'content:manage',
  CONTENT_MODERATE: 'content:moderate',
} as const;

export type AdminPermission = typeof AdminPermissions[keyof typeof AdminPermissions];